import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { ItemListingService } from '../services/itemListingService';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/LoadingSpinner';
import { supabase } from '../services/supabaseClient';

interface ItemListingPageProps {}

const ItemListingPage: React.FC<ItemListingPageProps> = () => {
  const [itemName, setItemName] = useState('');
  const [userDescription, setUserDescription] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length + selectedImages.length > 5) {
      showToast('error', 'Maximum 5 images allowed');
      return;
    }

    const newImages = [...selectedImages, ...files];
    setSelectedImages(newImages);

    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews([...imagePreviews, ...newPreviews]);
  };

  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);

    // Revoke the object URL to free memory
    URL.revokeObjectURL(imagePreviews[index]);

    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!itemName.trim()) {
      showToast('error', 'Please enter an item name');
      return;
    }

    if (selectedImages.length === 0) {
      showToast('error', 'Please select at least one image');
      return;
    }

    // Check authentication before proceeding
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    console.log('Auth check in component:', { user: user?.id, authError });

    if (!user) {
      showToast('error', 'You must be logged in to create items');
      return;
    }

    setIsAnalyzing(true);

    try {
      const result = await ItemListingService.createItemWithAnalysis(
        itemName.trim(),
        selectedImages,
        userDescription.trim() || undefined
      );

      setAnalysisResult(result);
      showToast('success', 'Item analyzed and added to inventory!');

      // Navigate to inventory after a short delay
      setTimeout(() => {
        navigate('/inventory');
      }, 2000);

    } catch (error) {
      console.error('Error creating item:', error);
      showToast('error', 'Failed to analyze item. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const resetForm = () => {
    setItemName('');
    setUserDescription('');
    setSelectedImages([]);
    setImagePreviews([]);
    setAnalysisResult(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Create Item Listing
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Upload photos and let AI generate a professional listing for your item
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Item Name */}
        <div>
          <label htmlFor="itemName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Item Name *
          </label>
          <input
            type="text"
            id="itemName"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="e.g., Nike Air Force 1 High"
            required
          />
        </div>

        {/* User Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Additional Description (Optional)
          </label>
          <textarea
            id="description"
            value={userDescription}
            onChange={(e) => setUserDescription(e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Any additional details about the item..."
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Item Photos * (Max 5)
          </label>

          {/* Upload Area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
          >
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Click to upload images or drag and drop
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              PNG, JPG up to 10MB each
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />

          {/* Image Previews */}
          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={isAnalyzing || selectedImages.length === 0}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAnalyzing ? (
              <div className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                Analyzing Item...
              </div>
            ) : (
              'Generate Listing'
            )}
          </button>

          <button
            type="button"
            onClick={resetForm}
            className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Reset
          </button>
        </div>
      </form>

      {/* Analysis Results */}
      {analysisResult && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">
            ✅ Item Successfully Analyzed!
          </h3>

          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-200">Generated Title:</h4>
              <p className="text-green-700 dark:text-green-300 mt-1">
                {analysisResult.ai_analysis?.title || 'Title not available'}
              </p>
            </div>

            <div>
              <h4 className="font-medium text-green-800 dark:text-green-200">Suggested Price:</h4>
              <p className="text-green-700 dark:text-green-300 mt-1">
                ${analysisResult.suggested_price?.toFixed(2) || 'Price not available'}
              </p>
            </div>

            <div>
              <h4 className="font-medium text-green-800 dark:text-green-200">Condition:</h4>
              <p className="text-green-700 dark:text-green-300 mt-1 capitalize">
                {analysisResult.condition?.replace('_', ' ') || 'Condition not assessed'}
              </p>
            </div>
          </div>

          <p className="text-sm text-green-600 dark:text-green-400 mt-4">
            Redirecting to inventory in a few seconds...
          </p>
        </div>
      )}
    </div>
  );
};

export default ItemListingPage;
